import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import {
  getFeedbackFieldForLocale,
  IExtraData,
} from "./schema.propertyApplicationNotEligible.types";

/**
 * The language this candidate reads, as one of the three the office writes in.
 *
 * Contacts do not all carry a tidy `en_UK`. `en_US` is stored too, which is why
 * the broker's notification preferences are read under that key as well, and a
 * bare `en` is no surprise either. Matching on the language rather than on the
 * whole tag keeps those together: otherwise an `en_US` candidate gets a box of
 * their own, labelled with the generic fallback because no English label
 * matches, sitting beside the `en_UK` box saying the same thing.
 */
const LOCALE_BY_LANGUAGE: { [language: string]: string } = {
  nl: ELocale.NL,
  fr: ELocale.FR,
  en: ELocale.EN,
};

export const getApplicationLocale = (application: IApplication): string => {
  const stored = `${application?.contacts?.[0]?.language || ""}`;
  const language = stored.toLowerCase().split(/[-_]/)[0];

  // Both fallbacks mirror the API, so the subject it translates and the body
  // sent from here always land in the same language: nothing stored means
  // Dutch, and something stored that is not one of the three means English.
  // Diverging here would reintroduce the exact split this ticket is about, on
  // the rarest candidates and therefore the ones nobody would notice.
  if (!stored) {
    return ELocale.NL;
  }

  return LOCALE_BY_LANGUAGE[language] || ELocale.EN;
};

/**
 * The languages actually being written to, in the order the candidates stand,
 * with how many of them read each.
 *
 * Only the languages in the selection: a box for French on a screen of Dutch
 * candidates is a box that can never be sent, and three boxes to move one
 * candidate would be three times the reading for no choice at all.
 */
export const getLocalesInSelection = (
  applications: IApplication[],
): { locale: string; count: number }[] => {
  const counts: { [locale: string]: number } = {};

  applications.forEach(application => {
    const locale = getApplicationLocale(application);
    counts[locale] = (counts[locale] || 0) + 1;
  });

  return Object.keys(counts).map(locale => ({
    locale,
    count: counts[locale],
  }));
};

/**
 * The message this broker set for this step in one language, ready to be read
 * and edited.
 *
 * Everything that is the same for every reader is filled in, because the broker
 * is checking a message rather than proofreading a template. The recipient's
 * name is the one thing left as a marker: the send resolves it per candidate,
 * so one text can greet several people each by their own name.
 *
 * It stays a marker even where only one candidate reads this language. Filling
 * it in there would put a name in one box and a marker in the one below it, and
 * a broker comparing the two would fairly wonder which of them is going to be
 * personal.
 */
const buildTenantFeedbackText = (
  extraData: IExtraData,
  locale: string,
): string | undefined => {
  const { localeMessages, user, property, applications } = extraData;

  const localeMessage = localeMessages[locale];

  if (!localeMessage) {
    return undefined;
  }

  const params: { [key: string]: string | null | undefined } = {
    address: formatAddress(property),
    applicant: "{{applicants}}",
    actor: applications[0]?.broker?.name || getName(user),

    propertyId: `${property.id}`,
    propertyInfoId: `${property.propertyInfoId}`,

    propertyReference: property.reference,
    propertyDescription: property.description,
    propertyName: property.name,

    refDewaele: property.refDewaele,
    refMaxImmo: property.refMaxImmo,
    refOmnicasa: property.refOmnicasa,
    refRealSuite: property.refRealSuite,
    refSkarabee: property.refSkarabee,
    refSweepbright: property.refSweepbright,
    refWhise: property.refWhise,
    refZabun: property.refZabun,

    // NOTE: {{bookingUrl}} is intentionally left unresolved. The backend detects
    // the marker and renders it as a real "book a visit" CTA button in the email
    // (splitting the body around it), so it must survive to the API as-is.
  };

  let result = localeMessage;
  Object.keys(params).forEach(key => {
    result = result.replaceAll(`{{${key}}}`, params[key] || "");
  });

  return result;
};

export const getInitialValues = (extraData: IExtraData) => {
  const values: { [field: string]: string | undefined } = {};

  getLocalesInSelection(extraData.applications).forEach(({ locale }) => {
    values[getFeedbackFieldForLocale(locale)] = buildTenantFeedbackText(
      extraData,
      locale,
    );
  });

  return values;
};

/**
 * What each candidate is sent, keyed by application id.
 *
 * Whatever stands in the box for that candidate's language, edited by the
 * broker or not. A language the broker left empty, because the office never
 * set a message for it, sends nothing to those candidates, exactly as before.
 */
export const getTenantFeedbackTextPerApplication = (
  extraData: IExtraData,
  formData: { [field: string]: any },
): { [applicationId: string]: string | undefined } => {
  const result: { [applicationId: string]: string | undefined } = {};

  extraData.applications.forEach(application => {
    const field = getFeedbackFieldForLocale(getApplicationLocale(application));
    const text = formData[field];

    result[application.id] = text === "" ? undefined : text;
  });

  return result;
};
